import { useEffect, useMemo, useRef, useState } from 'react';
import { Modale } from '../ui/Modale';
import {
  CLASSE_CHAMP,
  CLASSE_CHAMP_FAUTIF,
  Case,
  Champ,
  CodePostalEtCommune,
  Section,
} from '../ui/ChampsDossier';
import {
  Icone,
  Progression,
  TRACE_ALERTE,
  TRACE_COCHE,
  TRACE_FLECHE_DROITE,
  TRACE_FLECHE_GAUCHE,
} from '../ui/AssistantEtapes';
import { detailsRefus, libelleChamp } from '../../lib/erreurs-api';
import { aujourdhui, minoriteALEntree } from '../../lib/dates-civiles';
import { BoutonPrincipal, BoutonSecondaire, MessageErreur } from '../ui/Primitives';
import { useReferentiels, type CommunePostale } from '../../lib/useDocuments';
import {
  LONGUEUR_NIR_AFFICHEE,
  chiffresNir,
  formaterNir,
  formaterTelephone,
  longueurTelephoneAffichee,
  nirIncomplet,
  telephoneIncomplet,
} from '../../lib/saisie-numeros';
import { useSites } from '../../lib/useSites';
import { ErreurApi } from '../../lib/api';
import { useDemanderModification, useModificationsDuDossier } from '../../lib/useModificationsSensibles';
import { useToast } from '../ui/useToast';
import { AvisValidation, ChampMotif, motifValide } from '../modifications-sensibles/ElementsDemande';
import {
  useConseillers,
  useModifierDossier,
  type ConseillerAssignable,
  type Dossier,
  type EntreeModificationDossier,
  type Mobilite,
  type ModeDossier,
} from '../../lib/useApprenants';

/**
 * Édition du dossier apprenant (CDC §4.3).
 *
 * Le dossier se complète au fil du parcours : l'inscription n'exige que le nom,
 * le prénom et l'antenne, tout le reste arrive ensuite — par téléphone, au
 * guichet, ou à la lecture des pièces déposées. Cet écran est donc le lieu
 * normal de la saisie, pas une correction exceptionnelle.
 *
 * Il suit les mêmes étapes que l'inscription, et pour la même raison : une
 * trentaine de rubriques d'affilée dans une fenêtre décourage la saisie. Les
 * découper autrement qu'à l'inscription obligerait en plus à réapprendre le
 * même formulaire deux fois.
 *
 * Le **statut** découle de l'avancement et des pièces validées : le saisir à
 * la main le ferait mentir. Le **type de formation (mode CFA / OF)** et la
 * **formation** sont quant à eux modifiables ici comme à l'inscription.
 *
 * Deux effets de bord à connaître, tous deux assurés par l'API : cocher RQTH ou
 * « hébergé par un tiers » ajoute les pièces conditionnelles correspondantes, et
 * modifier une date recalcule la minorité à l'entrée.
 *
 * Entré en formation, le dossier ne corrige plus directement son identité, sa
 * RQTH ni son rattachement (TF-437) : à l'enregistrement, ces rubriques partent
 * en demande à la direction, motif à l'appui, et les autres s'enregistrent tout
 * de suite. Lesquelles, et depuis quand, c'est l'API qui le dit.
 */
export function FormulaireDossier({
  ouvert,
  dossier,
  onFermer,
}: {
  ouvert: boolean;
  dossier: Dossier;
  onFermer: () => void;
}): JSX.Element {
  const modification = useModifierDossier();
  const { data: sites } = useSites();
  const { data: referentiels } = useReferentiels();
  // Chargés à l'ouverture seulement : la liste ne sert qu'à ce formulaire.
  const { data: conseillers } = useConseillers(ouvert);

  const sensibles = useModificationsDuDossier(dossier.identite.id, ouvert);
  const demander = useDemanderModification();
  const toast = useToast();
  // Rubriques sensibles changées, en attente du motif de la demande, et le reste
  // de la saisie, qui s'enregistre avec elles.
  const [aValider, setAValider] = useState<{
    sensibles: Record<string, unknown>;
    libres: EntreeModificationDossier;
  } | null>(null);
  const [motifDemande, setMotifDemande] = useState('');
  const libellesSensibles = new Map((sensibles.data?.champs ?? []).map((champ) => [champ.champ, champ.libelle]));
  const engage = sensibles.data?.engage ?? false;
  const directe = sensibles.data?.validationDirecte ?? false;
  const demandeDossier = sensibles.data?.enAttente.find((demande) => demande.type === 'DOSSIER') ?? null;

  const [valeurs, setValeurs] = useState<Etat>(() => etatDepuisDossier(dossier));
  const [etape, setEtape] = useState(0);
  const [manques, setManques] = useState<string[]>([]);
  const etaitOuvert = useRef(false);
  // Valeurs telles qu'elles étaient à l'ouverture : c'est à elles que la saisie
  // se compare pour n'envoyer que ce que l'utilisateur a réellement changé. Se
  // comparer au dossier rechargé ferait renvoyer — et donc écraser — ce qu'un
  // autre poste aurait modifié entre-temps sans que personne n'y touche ici.
  const ouverture = useRef<Etat>(valeurs);

  // L'état repart du dossier **à l'ouverture seulement**. Ce composant reste
  // monté avec l'écran, et le dossier est une requête : toute invalidation —
  // une pièce déposée, une alerte traitée, une relance envoyée dans un bloc
  // voisin — en renvoie un objet neuf. Se recaler dessus à chaque fois effaçait
  // la saisie en cours sans rien dire, et l'enregistrement repartait alors sans
  // aucune modification à transmettre.
  useEffect(() => {
    if (!ouvert) {
      etaitOuvert.current = false;
      return;
    }
    if (etaitOuvert.current) return;
    etaitOuvert.current = true;
    const depart = etatDepuisDossier(dossier);
    ouverture.current = depart;
    setValeurs(depart);
    setEtape(0);
    setManques([]);
    setAValider(null);
    setMotifDemande('');
  }, [ouvert, dossier]);

  const modifier = <Cle extends keyof Etat>(cle: Cle, valeur: Etat[Cle]): void =>
    setValeurs((precedent) => ({ ...precedent, [cle]: valeur }));

  const sitesDisponibles = sites?.sites ?? [];
  const formations = referentiels?.formations ?? [];
  const communes = referentiels?.communesReunion ?? [];
  const nomenclatures = referentiels?.cerfaApprenti;

  // Le bloc CERFA suit le type de formation (mode CFA). En mode OF, il n'est
  // pas affiché ni transmis.
  const cerfa = valeurs.mode === 'CFA';

  // La minorité se recalcule sur la saisie en cours pour que l'étape apparaisse
  // dès la date corrigée ; sans date saisie, c'est l'API qui a tranché.
  const mineur = valeurs.dateNaissance
    ? minoriteALEntree(valeurs.dateNaissance, valeurs.dateEntree)
    : dossier.situation.mineur;

  // Un représentant déjà enregistré reste affiché même sur un(e) majeur(e) :
  // masquer l'étape le rendrait invisible et impossible à corriger ou à
  // effacer, alors qu'il part sur le CERFA. La décision se prend sur le dossier
  // chargé, jamais sur la saisie en cours : vider le dernier champ ferait
  // autrement disparaître l'étape sous la frappe.
  const representantRenseigne = CHAMPS_REPRESENTANT.some(
    (cle) => (dossier.cerfa?.representant[CLE_REPRESENTANT[cle]] ?? '').trim() !== '',
  );
  const representantAttendu = cerfa && (mineur || representantRenseigne);

  const etapes = useMemo(
    () =>
      [
        'Identité',
        'Adresse',
        'Situation',
        ...(representantAttendu ? ['Représentant légal'] : []),
      ] as string[],
    [representantAttendu],
  );

  // Une date corrigée peut retirer la dernière étape sous les pieds : l'index
  // resterait hors des étapes restantes, donc un corps de fenêtre vide.
  const etapeCourante = Math.min(etape, etapes.length - 1);
  const derniere = etapeCourante === etapes.length - 1;

  // Rubriques refusées par l'API, telles qu'elle les nomme.
  const refus = detailsRefus(modification.error);

  const fermer = (): void => {
    modification.reset();
    demander.reset();
    onFermer();
  };

  /** Rubriques obligatoires vidées, dans l'ordre du formulaire. */
  const manquants = (): string[] => {
    const absents: string[] = [];
    if (valeurs.nom.trim() === '') absents.push('le nom de naissance');
    if (valeurs.prenom.trim() === '') absents.push('le premier prénom');
    if (valeurs.siteCode === '') absents.push("l'antenne de rattachement");
    return absents;
  };

  /**
   * Saisies commencées mais inachevées, avec l'étape où les reprendre.
   *
   * L'API refuse un NIR de treize caractères — sa clé ne peut pas se vérifier :
   * le dire ici, à côté du champ, évite un 400 générique au retour.
   */
  const inacheves = (): { texte: string; etape: number }[] => {
    const soucis: { texte: string; etape: number }[] = [];
    if (cerfa && nirIncomplet(valeurs.nir)) {
      soucis.push({
        texte: `le NIR est incomplet (${chiffresNir(valeurs.nir).length} caractères sur 15)`,
        etape: etapes.indexOf('Identité'),
      });
    }
    if (telephoneIncomplet(valeurs.telephone)) {
      soucis.push({
        texte: 'le téléphone est incomplet (10 chiffres attendus)',
        etape: etapes.indexOf('Adresse'),
      });
    }
    return soucis;
  };

  const avancer = (): void => {
    if (etapeCourante === 0) {
      const absents = manquants();
      if (absents.length > 0) {
        setManques(absents);
        return;
      }
    }
    setManques([]);
    setEtape(etapeCourante + 1);
  };

  const envoyer = (evenement: React.FormEvent): void => {
    evenement.preventDefault();

    // Les champs obligatoires vivent à la première étape, qui n'est plus montée
    // au moment de l'envoi : la validation native du navigateur ne les voit
    // pas. On les revérifie à la main, et on ramène là où les corriger.
    const absents = manquants();
    if (absents.length > 0) {
      setManques(absents);
      setEtape(0);
      return;
    }

    const commences = inacheves();
    if (commences.length > 0) {
      setManques(commences.map((souci) => souci.texte));
      setEtape(commences[0]!.etape);
      return;
    }
    setManques([]);

    const charge = chargeUtile(valeurs, ouverture.current);
    if (Object.keys(charge).length === 0) {
      setManques(['aucune modification à enregistrer']);
      return;
    }

    if (engage) {
      const entrees = Object.entries(charge);
      const sensiblesChanges = Object.fromEntries(entrees.filter(([champ]) => libellesSensibles.has(champ)));
      if (Object.keys(sensiblesChanges).length > 0) {
        demander.reset();
        setMotifDemande('');
        setAValider({
          sensibles: sensiblesChanges,
          libres: Object.fromEntries(
            entrees.filter(([champ]) => !libellesSensibles.has(champ)),
          ) as EntreeModificationDossier,
        });
        return;
      }
    }

    modification.mutate(
      { id: dossier.identite.id, valeurs: charge },
      {
        onSuccess: () => {
          modification.reset();
          onFermer();
        },
      },
    );
  };

  /** Enregistre le reste de la saisie, puis envoie les rubriques sensibles à la direction. */
  const confirmerDemande = async (): Promise<void> => {
    if (!aValider) return;
    try {
      if (Object.keys(aValider.libres).length > 0) {
        await modification.mutateAsync({ id: dossier.identite.id, valeurs: aValider.libres });
      }
      const resultat = await demander.mutateAsync({
        type: 'DOSSIER',
        apprenantId: dossier.identite.id,
        champs: aValider.sensibles,
        motif: motifDemande.trim(),
      });
      toast(resultat.message);
      setAValider(null);
      fermer();
    } catch {
      // Le refus de l'API est affiché par la mutation qui l'a reçu.
    }
  };

  return (
    <Modale
      titre="Modifier le dossier"
      sousTitre={`${dossier.identite.prenom} ${dossier.identite.nom} · les pièces attendues suivent la situation renseignée`}
      ouverte={ouvert}
      onFermer={fermer}
      largeur="max-w-3xl"
    >
      <Progression etapes={etapes} courante={etapeCourante} onAller={setEtape} />

      <form onSubmit={envoyer} className="space-y-5 px-4 py-5 sm:px-6">
        {modification.error && (
          <div className="space-y-2">
            <MessageErreur
              message={
                modification.error instanceof ErreurApi
                  ? modification.error.message
                  : "Les modifications n'ont pas pu être enregistrées."
              }
            />
            {/* L'API dit quelle rubrique elle refuse et pourquoi : sans ce
                détail, il faut repasser les étapes à l'aveugle pour la trouver. */}
            {refus.length > 0 && (
              <ul className="space-y-1 rounded-xl bg-red-50 px-3 py-2 text-[11px] text-red-700">
                {refus.map((detail) => (
                  <li key={`${detail.champ}-${detail.message}`}>
                    <span className="font-semibold">{libelleChamp(detail.champ)}</span> :{' '}
                    {detail.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {manques.length > 0 && (
          <MessageErreur message={`À reprendre avant d’enregistrer : ${manques.join(', ')}.`} />
        )}

        {aValider ? (
          <ValidationDirection
            champs={Object.keys(aValider.sensibles).map((champ) => libellesSensibles.get(champ) ?? libelleChamp(champ))}
            autres={Object.keys(aValider.libres).length}
            raison={sensibles.data?.raison ?? null}
            directe={directe}
            demandeEnCours={demandeDossier !== null}
            motif={motifDemande}
            onMotif={setMotifDemande}
            erreur={
              demander.error instanceof ErreurApi
                ? demander.error.message
                : demander.error
                  ? 'La demande n’a pas pu être envoyée.'
                  : null
            }
            enCours={modification.isPending || demander.isPending}
            onRevenir={() => setAValider(null)}
            onConfirmer={() => void confirmerDemande()}
          />
        ) : (
          <>
        {engage && etapes[etapeCourante] === 'Identité' && (
          <p className="rounded-xl bg-codialis-sand px-3 py-2 text-xs text-codialis-navy">
            {directe
              ? 'Dossier entré en formation : l’identité, la RQTH et le rattachement corrigés ici sont inscrits au registre des modifications sensibles, avec un motif.'
              : 'Dossier entré en formation : l’identité, la RQTH et le rattachement corrigés ici partent en demande à la direction, avec un motif. Le reste s’enregistre tout de suite.'}
          </p>
        )}

        {etapes[etapeCourante] === 'Identité' && (
          <EtapeIdentite
            valeurs={valeurs}
            modifier={modifier}
            cerfa={cerfa}
            sites={sitesDisponibles}
            conseillers={conseillers?.conseillers ?? []}
            regimesSociaux={nomenclatures?.regimesSociaux ?? []}
          />
        )}

        {etapes[etapeCourante] === 'Adresse' && (
          <EtapeAdresse valeurs={valeurs} modifier={modifier} cerfa={cerfa} communes={communes} />
        )}

        {etapes[etapeCourante] === 'Situation' && (
          <EtapeSituation
            valeurs={valeurs}
            modifier={modifier}
            cerfa={cerfa}
            formations={formations}
            situations={nomenclatures?.situationsAvantContrat ?? []}
            classes={nomenclatures?.dernieresClasses ?? []}
            diplomes={nomenclatures?.diplomes ?? []}
          />
        )}

        {etapes[etapeCourante] === 'Représentant légal' && (
          <EtapeRepresentant
            valeurs={valeurs}
            modifier={modifier}
            mineur={mineur}
            communes={communes}
          />
        )}

        {/* Barre d'actions collante : sur tablette, le pouce reste en bas de
            l'écran et une étape peut dépasser la hauteur de la fenêtre. */}
        <div className="sticky bottom-0 -mx-4 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-white px-4 pb-1 pt-4 sm:-mx-6 sm:px-6">
          <BoutonSecondaire onClick={fermer} disabled={modification.isPending}>
            Annuler
          </BoutonSecondaire>
          <div className="flex items-center gap-2">
            {etapeCourante > 0 && (
              <BoutonSecondaire onClick={() => setEtape(etapeCourante - 1)}>
                <span className="flex items-center gap-1.5">
                  <Icone trace={TRACE_FLECHE_GAUCHE} classe="h-4 w-4" />
                  Précédent
                </span>
              </BoutonSecondaire>
            )}
            {derniere ? (
              <BoutonPrincipal type="submit" disabled={modification.isPending}>
                <span className="flex items-center gap-1.5">
                  <Icone trace={TRACE_COCHE} classe="h-4 w-4" />
                  {modification.isPending ? 'Enregistrement…' : 'Enregistrer'}
                </span>
              </BoutonPrincipal>
            ) : (
              <BoutonPrincipal type="button" onClick={avancer}>
                <span className="flex items-center gap-1.5">
                  Suivant
                  <Icone trace={TRACE_FLECHE_DROITE} classe="h-4 w-4" />
                </span>
              </BoutonPrincipal>
            )}
          </div>
        </div>
          </>
        )}
      </form>
    </Modale>
  );
}

/**
 * Rubriques sensibles changées sur un dossier entré en formation (TF-437) :
 * elles partent en demande à la direction, motif exigé, et le reste de la
 * saisie s'enregistre avec elles. La direction, elle, les applique.
 */
function ValidationDirection({
  champs,
  autres,
  raison,
  directe,
  demandeEnCours,
  motif,
  onMotif,
  erreur,
  enCours,
  onRevenir,
  onConfirmer,
}: {
  champs: string[];
  autres: number;
  raison: string | null;
  directe: boolean;
  demandeEnCours: boolean;
  motif: string;
  onMotif: (motif: string) => void;
  erreur: string | null;
  enCours: boolean;
  onRevenir: () => void;
  onConfirmer: () => void;
}): JSX.Element {
  return (
    <div className="space-y-4">
      <AvisValidation directe={directe} />
      <div className="space-y-2 rounded-2xl border border-gray-100 bg-white p-4">
        <p className="text-sm font-semibold text-codialis-navy">
          {directe ? 'Rubriques sensibles corrigées' : 'Rubriques soumises à la validation de la direction'}
        </p>
        <ul className="list-disc space-y-0.5 pl-5 text-sm text-codialis-navy">
          {champs.map((champ) => (
            <li key={champ}>{champ}</li>
          ))}
        </ul>
        {raison && (
          <p className="text-xs text-codialis-gray">
            Elles figurent sur les documents déjà produits : {raison}.
          </p>
        )}
        {autres > 0 && (
          <p className="text-xs text-codialis-gray">
            {autres > 1
              ? `Les ${autres} autres modifications s’enregistrent tout de suite.`
              : 'L’autre modification s’enregistre tout de suite.'}
          </p>
        )}
      </div>
      {demandeEnCours && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Une demande attend déjà la direction sur ce dossier : elle doit être décidée ou retirée avant une autre.
        </p>
      )}
      <ChampMotif valeur={motif} onChange={onMotif} directe={directe} placeholder="Acte de naissance reçu, erreur de saisie…" />
      {erreur && <MessageErreur message={erreur} />}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-4">
        <BoutonSecondaire onClick={onRevenir} disabled={enCours}>
          Revenir à la saisie
        </BoutonSecondaire>
        <BoutonPrincipal onClick={onConfirmer} disabled={enCours || !motifValide(motif)}>
          {enCours
            ? 'Envoi…'
            : directe
              ? 'Enregistrer et appliquer'
              : 'Enregistrer et demander la validation'}
        </BoutonPrincipal>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Étapes
// ---------------------------------------------------------------------------

type Modificateur = <Cle extends keyof Etat>(cle: Cle, valeur: Etat[Cle]) => void;

interface EntreeNomenclature {
  code: string;
  libelle: string;
  sansDiplome?: boolean;
}

function EtapeIdentite({
  valeurs,
  modifier,
  cerfa,
  sites,
  conseillers,
  regimesSociaux,
}: {
  valeurs: Etat;
  modifier: Modificateur;
  cerfa: boolean;
  sites: { code: string; nom: string }[];
  conseillers: ConseillerAssignable[];
  regimesSociaux: EntreeNomenclature[];
}): JSX.Element {
  // Un référent rattaché hors périmètre — antenne quittée, compte désactivé —
  // n'est pas dans la liste servie. L'ajouter évite que la liste affiche
  // « Aucun » sur un dossier qui a bien un référent, et que l'enregistrement
  // le détache sans qu'on l'ait demandé.
  const referentHorsListe =
    valeurs.conseillerId !== '' &&
    !conseillers.some((conseiller) => conseiller.id === valeurs.conseillerId);

  return (
    <>
      <Section titre="Dossier">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Champ libelle="Antenne de rattachement" obligatoire>
            <select
              required
              value={valeurs.siteCode}
              onChange={(evenement) => modifier('siteCode', evenement.target.value)}
              className={CLASSE_CHAMP}
            >
              <option value="">À choisir</option>
              {sites.map((site) => (
                <option key={site.code} value={site.code}>
                  {site.nom} ({site.code})
                </option>
              ))}
            </select>
          </Champ>
          <Champ
            libelle="Type de formation"
            aide="CFA : contrat d’apprentissage et CERFA. OF : session de formation et certificat."
          >
            <select
              value={valeurs.mode}
              onChange={(evenement) => modifier('mode', evenement.target.value as ModeDossier)}
              className={CLASSE_CHAMP}
            >
              <option value="CFA">CFA · apprentissage</option>
              <option value="OF">OF · organisme de formation</option>
            </select>
          </Champ>
          <div className="sm:col-span-2 min-w-0">
            <Champ
              libelle="Conseiller référent"
              aide="La personne que le candidat rappelle, et qui répond de l’avancement du dossier."
            >
              <select
                value={valeurs.conseillerId}
                onChange={(evenement) => modifier('conseillerId', evenement.target.value)}
                className={CLASSE_CHAMP}
              >
                <option value="">Aucun</option>
                {referentHorsListe && (
                  <option value={valeurs.conseillerId}>
                    {valeurs.conseillerNom} (hors périmètre)
                  </option>
                )}
                {conseillers.map((conseiller) => (
                  <option key={conseiller.id} value={conseiller.id}>
                    {conseiller.prenom} {conseiller.nom}
                  </option>
                ))}
              </select>
            </Champ>
          </div>
        </div>
      </Section>

      <Section titre="Identité">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Champ libelle={cerfa ? 'Nom de naissance' : 'Nom'} obligatoire>
            <input
              required
              maxLength={80}
              value={valeurs.nom}
              onChange={(evenement) => modifier('nom', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          {cerfa && (
            <Champ libelle="Nom d’usage" aide="À ne remplir que s’il diffère du nom de naissance.">
              <input
                maxLength={80}
                value={valeurs.nomUsage}
                onChange={(evenement) => modifier('nomUsage', evenement.target.value)}
                className={CLASSE_CHAMP}
              />
            </Champ>
          )}
          <Champ libelle={cerfa ? 'Premier prénom selon l’état civil' : 'Prénom'} obligatoire>
            <input
              required
              maxLength={80}
              value={valeurs.prenom}
              onChange={(evenement) => modifier('prenom', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          {cerfa && (
            <Champ
              libelle="NIR (numéro de sécurité sociale)"
              aide="13 chiffres puis la clé sur 2. La clé est vérifiée à l’enregistrement."
              erreur={
                nirIncomplet(valeurs.nir)
                  ? `${chiffresNir(valeurs.nir).length} caractères sur 15`
                  : undefined
              }
            >
              <input
                inputMode="numeric"
                autoComplete="off"
                maxLength={LONGUEUR_NIR_AFFICHEE}
                placeholder="2 09 05 97 412 345 26"
                value={valeurs.nir}
                onChange={(evenement) => modifier('nir', formaterNir(evenement.target.value))}
                aria-invalid={nirIncomplet(valeurs.nir) || undefined}
                className={`${nirIncomplet(valeurs.nir) ? CLASSE_CHAMP_FAUTIF : CLASSE_CHAMP} font-mono tracking-wide`}
              />
            </Champ>
          )}
          <Champ
            libelle="Date de naissance"
            aide="Détermine la minorité à l’entrée, et donc les pièces du représentant légal."
          >
            <input
              type="date"
              max={aujourdhui()}
              value={valeurs.dateNaissance}
              onChange={(evenement) => modifier('dateNaissance', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Sexe">
            <select
              value={valeurs.sexe}
              onChange={(evenement) => modifier('sexe', evenement.target.value)}
              className={CLASSE_CHAMP}
            >
              <option value="">Non renseigné</option>
              <option value="M">Masculin</option>
              <option value="F">Féminin</option>
            </select>
          </Champ>
          {cerfa && (
            <Champ libelle="Département de naissance" aide="Code à deux ou trois chiffres : 974, 2A…">
              <input
                maxLength={3}
                placeholder="974"
                value={valeurs.departementNaissance}
                onChange={(evenement) => modifier('departementNaissance', evenement.target.value)}
                className={CLASSE_CHAMP}
              />
            </Champ>
          )}
          <Champ libelle={cerfa ? 'Commune de naissance' : 'Lieu de naissance'}>
            <input
              maxLength={120}
              value={valeurs.lieuNaissance}
              onChange={(evenement) => modifier('lieuNaissance', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Nationalité">
            <input
              maxLength={60}
              value={valeurs.nationalite}
              onChange={(evenement) => modifier('nationalite', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ
            libelle="INE"
            aide="Identifiant national élève : 10 chiffres puis une lettre ou un chiffre."
          >
            <input
              maxLength={11}
              autoComplete="off"
              placeholder="0123456789A"
              value={valeurs.ine}
              onChange={(evenement) => modifier('ine', evenement.target.value.toUpperCase())}
              className={`${CLASSE_CHAMP} font-mono tracking-wide`}
            />
          </Champ>
          {cerfa && (
            <Champ libelle="Régime social">
              <select
                value={valeurs.regimeSocial}
                onChange={(evenement) => modifier('regimeSocial', evenement.target.value)}
                className={CLASSE_CHAMP}
              >
                <option value="">Non renseigné</option>
                {regimesSociaux.map((regime) => (
                  <option key={regime.code} value={regime.code}>
                    {regime.libelle}
                  </option>
                ))}
              </select>
            </Champ>
          )}
        </div>
      </Section>
    </>
  );
}

function EtapeAdresse({
  valeurs,
  modifier,
  cerfa,
  communes,
}: {
  valeurs: Etat;
  modifier: Modificateur;
  cerfa: boolean;
  communes: CommunePostale[];
}): JSX.Element {
  // Ligne d'adresse héritée d'une saisie antérieure à sa découpe : elle existe
  // sans qu'aucune composante ne soit renseignée.
  const adresseAReprendre =
    cerfa &&
    valeurs.adresse.trim() !== '' &&
    valeurs.numeroVoie === '' &&
    valeurs.voie === '' &&
    valeurs.complementAdresse === '';

  return (
    <>
      {cerfa ? (
        <Section
          titre="Adresse"
          aide="Le numéro et la voie sont séparés comme sur le CERFA ; la ligne d’adresse affichée ailleurs est recomposée automatiquement."
        >
          {/* Un dossier ancien peut n'avoir que la ligne d'adresse, saisie avant
              qu'elle ne soit éclatée. La montrer évite de croire l'adresse
              perdue, et dit ce que la nouvelle saisie va remplacer. */}
          {adresseAReprendre && (
            <p className="mb-3 rounded-lg border border-gray-200 bg-codialis-sand/60 p-3 text-[11px] text-codialis-gray">
              Adresse enregistrée : <span className="font-semibold">{valeurs.adresse}</span>. La
              renseigner ci-dessous en numéro et voie remplacera cette ligne.
            </p>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
            <div className="sm:col-span-1 min-w-0">
              <Champ libelle="N°">
                <input
                  maxLength={10}
                  value={valeurs.numeroVoie}
                  onChange={(evenement) => modifier('numeroVoie', evenement.target.value)}
                  className={CLASSE_CHAMP}
                />
              </Champ>
            </div>
            <div className="sm:col-span-5 min-w-0">
              <Champ libelle="Voie">
                <input
                  maxLength={160}
                  autoComplete="address-line1"
                  value={valeurs.voie}
                  onChange={(evenement) => modifier('voie', evenement.target.value)}
                  className={CLASSE_CHAMP}
                />
              </Champ>
            </div>
            <div className="sm:col-span-6 min-w-0">
              <Champ libelle="Complément" aide="Bâtiment, résidence, lieu-dit.">
                <input
                  maxLength={160}
                  autoComplete="address-line2"
                  value={valeurs.complementAdresse}
                  onChange={(evenement) => modifier('complementAdresse', evenement.target.value)}
                  className={CLASSE_CHAMP}
                />
              </Champ>
            </div>
            <CodePostalEtCommune
              identifiant="dossier-apprenant"
              communes={communes}
              codePostal={valeurs.codePostal}
              commune={valeurs.ville}
              onChange={(codePostal, commune) => {
                modifier('codePostal', codePostal);
                modifier('ville', commune);
              }}
            />
          </div>
        </Section>
      ) : (
        // En OF, l'adresse n'a pas à suivre la découpe du CERFA : elle reste la
        // ligne unique déjà saisie partout ailleurs.
        <Section titre="Adresse">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
            <div className="sm:col-span-6 min-w-0">
              <Champ libelle="Adresse">
                <input
                  maxLength={200}
                  value={valeurs.adresse}
                  onChange={(evenement) => modifier('adresse', evenement.target.value)}
                  className={CLASSE_CHAMP}
                />
              </Champ>
            </div>
            <CodePostalEtCommune
              identifiant="dossier-apprenant"
              communes={communes}
              codePostal={valeurs.codePostal}
              commune={valeurs.ville}
              onChange={(codePostal, commune) => {
                modifier('codePostal', codePostal);
                modifier('ville', commune);
              }}
            />
          </div>
        </Section>
      )}

      <Section titre="Contact">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Champ
            libelle="Téléphone"
            aide="10 chiffres, ou format international (+262…)."
            erreur={
              telephoneIncomplet(valeurs.telephone)
                ? 'Numéro incomplet : 10 chiffres attendus.'
                : undefined
            }
          >
            <input
              type="tel"
              inputMode="tel"
              maxLength={longueurTelephoneAffichee(valeurs.telephone)}
              autoComplete="tel"
              placeholder="06 92 55 12 09"
              value={valeurs.telephone}
              onChange={(evenement) =>
                modifier('telephone', formaterTelephone(evenement.target.value))
              }
              aria-invalid={telephoneIncomplet(valeurs.telephone) || undefined}
              className={telephoneIncomplet(valeurs.telephone) ? CLASSE_CHAMP_FAUTIF : CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Courriel" aide="Sert au lien d’accès et aux relances.">
            <input
              type="email"
              autoComplete="email"
              value={valeurs.email}
              onChange={(evenement) => modifier('email', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
        </div>
      </Section>
    </>
  );
}

function EtapeSituation({
  valeurs,
  modifier,
  cerfa,
  formations,
  situations,
  classes,
  diplomes,
}: {
  valeurs: Etat;
  modifier: Modificateur;
  cerfa: boolean;
  formations: { code: string; titre: string }[];
  situations: EntreeNomenclature[];
  classes: EntreeNomenclature[];
  diplomes: EntreeNomenclature[];
}): JSX.Element {
  // « Aucun diplôme » au CERFA et « Titulaire d'un diplôme » ne cohabitent pas :
  // la case suit le choix, pour que la copie d'un diplôme inexistant ne soit ni
  // attendue ni relancée. Elle se recoche si l'on revient sur ce choix.
  const sansDiplome = (code: string): boolean =>
    diplomes.some((diplome) => diplome.code === code && diplome.sansDiplome === true);
  const choisirDiplomeLePlusEleve = (code: string): void => {
    modifier('diplomeLePlusEleve', code);
    if (sansDiplome(code)) modifier('titulaireDiplome', false);
    else if (sansDiplome(valeurs.diplomeLePlusEleve)) modifier('titulaireDiplome', true);
  };

  return (
    <>
      <Section titre="Parcours visé">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Champ
            libelle="Type de formation"
            aide="CFA : apprentissage. OF : formation pro."
          >
            <select
              value={valeurs.mode}
              onChange={(evenement) => modifier('mode', evenement.target.value as ModeDossier)}
              className={CLASSE_CHAMP}
            >
              <option value="CFA">CFA · apprentissage</option>
              <option value="OF">OF · organisme de formation</option>
            </select>
          </Champ>
          <Champ libelle="Formation visée">
            <select
              value={valeurs.formationCode}
              onChange={(evenement) => modifier('formationCode', evenement.target.value)}
              className={CLASSE_CHAMP}
            >
              <option value="">À définir</option>
              {formations.map((formation) => (
                <option key={formation.code} value={formation.code}>
                  {formation.titre} ({formation.code})
                </option>
              ))}
            </select>
          </Champ>
          <Champ libelle="Date d’entrée prévue">
            <input
              type="date"
              value={valeurs.dateEntree}
              onChange={(evenement) => modifier('dateEntree', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ
            libelle="Date de fin prévue"
            aide="Fin du parcours de formation, distincte de la fin du contrat."
          >
            <input
              type="date"
              min={valeurs.dateEntree || undefined}
              value={valeurs.dateFinPrevue}
              onChange={(evenement) => modifier('dateFinPrevue', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          {/* La mobilité pèse au barème du matching : non renseignée, le
              candidat perd ces points sur chaque offre. */}
          <Champ libelle="Mobilité" aide="Critère du matching avec les offres d’entreprise.">
            <select
              value={valeurs.mobilite}
              onChange={(evenement) => modifier('mobilite', evenement.target.value)}
              className={CLASSE_CHAMP}
            >
              <option value="">Non renseignée</option>
              <option value="PERMIS_B">Permis B</option>
              <option value="TRANSPORTS_EN_COMMUN">Transports en commun</option>
              <option value="AUCUNE">Aucune</option>
            </select>
          </Champ>
        </div>
      </Section>

      {cerfa && (
        <Section
          titre="Situation avant ce contrat"
          aide="Codes du CERFA : le formulaire officiel attend le code, pas le libellé."
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Champ libelle="Situation avant ce contrat">
              <select
                value={valeurs.situationAvantContrat}
                onChange={(evenement) => modifier('situationAvantContrat', evenement.target.value)}
                className={CLASSE_CHAMP}
              >
                <option value="">Non renseignée</option>
                {situations.map((situation) => (
                  <option key={situation.code} value={situation.code}>
                    {situation.code} · {situation.libelle}
                  </option>
                ))}
              </select>
            </Champ>
            <Champ libelle="Dernier diplôme ou titre préparé">
              <select
                value={valeurs.dernierDiplomePrepare}
                onChange={(evenement) => modifier('dernierDiplomePrepare', evenement.target.value)}
                className={CLASSE_CHAMP}
              >
                <option value="">Non renseigné</option>
                {diplomes.map((diplome) => (
                  <option key={diplome.code} value={diplome.code}>
                    {diplome.code} · {diplome.libelle}
                  </option>
                ))}
              </select>
            </Champ>
            <Champ libelle="Dernière classe ou année suivie">
              <select
                value={valeurs.derniereClasseSuivie}
                onChange={(evenement) => modifier('derniereClasseSuivie', evenement.target.value)}
                className={CLASSE_CHAMP}
              >
                <option value="">Non renseignée</option>
                {classes.map((classe) => (
                  <option key={classe.code} value={classe.code}>
                    {classe.code} · {classe.libelle}
                  </option>
                ))}
              </select>
            </Champ>
            <Champ libelle="Diplôme ou titre le plus élevé obtenu">
              <select
                value={valeurs.diplomeLePlusEleve}
                onChange={(evenement) => choisirDiplomeLePlusEleve(evenement.target.value)}
                className={CLASSE_CHAMP}
              >
                <option value="">Non renseigné</option>
                {diplomes.map((diplome) => (
                  <option key={diplome.code} value={diplome.code}>
                    {diplome.code} · {diplome.libelle}
                  </option>
                ))}
              </select>
            </Champ>
            <div className="sm:col-span-2 min-w-0">
              <Champ
                libelle="Intitulé précis du dernier diplôme ou titre préparé"
                aide="En toutes lettres, tel qu’il sera reporté sur le CERFA."
              >
                <input
                  maxLength={200}
                  value={valeurs.intitulePrecisDernierDiplome}
                  onChange={(evenement) =>
                    modifier('intitulePrecisDernierDiplome', evenement.target.value)
                  }
                  className={CLASSE_CHAMP}
                />
              </Champ>
            </div>
          </div>
        </Section>
      )}

      {cerfa && (
        <Section titre="Déclarations de l’apprenti(e)">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Case
              libelle="Sportif(ve) de haut niveau"
              aide="Inscrit(e) sur la liste officielle"
              coche={valeurs.sportifHautNiveau}
              onBasculer={() => modifier('sportifHautNiveau', !valeurs.sportifHautNiveau)}
            />
            <Case
              libelle="Projet de création ou reprise d’entreprise"
              aide="Déclaré au moment du contrat"
              coche={valeurs.projetCreationEntreprise}
              onBasculer={() =>
                modifier('projetCreationEntreprise', !valeurs.projetCreationEntreprise)
              }
            />
          </div>
        </Section>
      )}

      <Section
        titre="Situation administrative"
        aide="Chaque case cochée ajoute une pièce justificative à la liste attendue."
      >
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Case
            libelle="Hébergé(e) par un tiers"
            aide="Attestation d’hébergement"
            coche={valeurs.hebergeParTiers}
            onBasculer={() => modifier('hebergeParTiers', !valeurs.hebergeParTiers)}
          />
          <Case
            libelle="RQTH"
            aide="Attestation de reconnaissance"
            coche={valeurs.rqth}
            onBasculer={() => modifier('rqth', !valeurs.rqth)}
          />
          <Case
            libelle="Déjà apprenti(e)"
            aide="Contrat d’apprentissage précédent"
            coche={valeurs.dejaApprenti}
            onBasculer={() => modifier('dejaApprenti', !valeurs.dejaApprenti)}
          />
          <Case
            libelle="Inscrit(e) à France Travail"
            aide="Attestation d’inscription"
            coche={valeurs.demandeurEmploi}
            onBasculer={() => modifier('demandeurEmploi', !valeurs.demandeurEmploi)}
          />
          <Case
            libelle="Titulaire d’un diplôme"
            aide="Copie du ou des diplômes"
            coche={valeurs.titulaireDiplome}
            onBasculer={() => modifier('titulaireDiplome', !valeurs.titulaireDiplome)}
          />
          <Case
            libelle="Carte étudiante des métiers faite"
            aide="Suivi administratif, sans pièce à déposer"
            coche={valeurs.carteEtudianteFaite}
            onBasculer={() => modifier('carteEtudianteFaite', !valeurs.carteEtudianteFaite)}
          />
          {/* Le matching commercial part de cet indicateur : coché à la main
              ici, il l'est aussi automatiquement à la rupture d'un contrat. */}
          <Case
            libelle="En recherche d’entreprise"
            aide="Fait remonter le candidat au matching commercial"
            coche={valeurs.rechercheEntreprise}
            onBasculer={() => modifier('rechercheEntreprise', !valeurs.rechercheEntreprise)}
          />
        </div>

        {/* Les droits attachés à la RQTH ne se posent qu'à défaut de RQTH :
            affichés en permanence, ils se cocheraient par habitude au-dessus
            d'une RQTH déjà déclarée, ce que le CERFA ne prévoit pas. */}
        {cerfa && !valeurs.rqth && (
          <div className="mt-3 rounded-lg border border-gray-200 bg-codialis-sand/60 p-3">
            <p className="text-xs font-semibold text-codialis-navy">
              À défaut de RQTH, droits attachés à la RQTH
            </p>
            <p className="mt-0.5 text-[11px] text-codialis-gray">Page 4 de la notice du CERFA.</p>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Case
                libelle="Équivalence jeunes"
                aide="Droits ouverts sans RQTH"
                coche={valeurs.rqthEquivalenceJeunes}
                onBasculer={() => modifier('rqthEquivalenceJeunes', !valeurs.rqthEquivalenceJeunes)}
              />
              <Case
                libelle="Extension BOE"
                aide="Bénéficiaire de l’obligation d’emploi"
                coche={valeurs.rqthExtensionBoe}
                onBasculer={() => modifier('rqthExtensionBoe', !valeurs.rqthExtensionBoe)}
              />
            </div>
          </div>
        )}
      </Section>
    </>
  );
}

function EtapeRepresentant({
  valeurs,
  modifier,
  mineur,
  communes,
}: {
  valeurs: Etat;
  modifier: Modificateur;
  mineur: boolean;
  communes: CommunePostale[];
}): JSX.Element {
  return (
    <Section
      titre="Représentant légal"
      aide="À renseigner si l’apprenti(e) est mineur(e) non émancipé(e)."
    >
      {mineur ? (
        <p className="mb-3 flex items-start gap-2 rounded-lg border border-codialis-mint/60 bg-codialis-mint/10 p-3 text-xs text-codialis-navy">
          <Icone trace={TRACE_ALERTE} classe="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            L’apprenti(e) est mineur(e) à l’entrée en formation : le représentant légal et ses
            pièces sont attendus.
          </span>
        </p>
      ) : (
        // Cas d'un représentant saisi puis d'une majorité atteinte ou d'une date
        // corrigée : l'étape reste ouverte pour pouvoir le corriger ou l'effacer.
        <p className="mb-3 text-xs text-codialis-gray">
          L’apprenti(e) est majeur(e) à l’entrée en formation. Ces coordonnées restent
          enregistrées et peuvent être effacées champ par champ.
        </p>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Champ libelle="Nom de naissance">
          <input
            maxLength={80}
            value={valeurs.representantNom}
            onChange={(evenement) => modifier('representantNom', evenement.target.value)}
            className={CLASSE_CHAMP}
          />
        </Champ>
        <Champ libelle="Prénom">
          <input
            maxLength={80}
            value={valeurs.representantPrenom}
            onChange={(evenement) => modifier('representantPrenom', evenement.target.value)}
            className={CLASSE_CHAMP}
          />
        </Champ>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-6">
        <div className="sm:col-span-1 min-w-0">
          <Champ libelle="N°">
            <input
              maxLength={10}
              value={valeurs.representantNumeroVoie}
              onChange={(evenement) => modifier('representantNumeroVoie', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
        </div>
        <div className="sm:col-span-5 min-w-0">
          <Champ libelle="Voie">
            <input
              maxLength={160}
              value={valeurs.representantVoie}
              onChange={(evenement) => modifier('representantVoie', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
        </div>
        <div className="sm:col-span-6 min-w-0">
          <Champ libelle="Complément">
            <input
              maxLength={160}
              value={valeurs.representantComplement}
              onChange={(evenement) => modifier('representantComplement', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
        </div>
        <CodePostalEtCommune
          identifiant="dossier-representant"
          communes={communes}
          codePostal={valeurs.representantCodePostal}
          commune={valeurs.representantCommune}
          onChange={(codePostal, commune) => {
            modifier('representantCodePostal', codePostal);
            modifier('representantCommune', commune);
          }}
        />
        <div className="sm:col-span-6 min-w-0">
          <Champ libelle="Courriel">
            <input
              type="email"
              value={valeurs.representantCourriel}
              onChange={(evenement) => modifier('representantCourriel', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// État et charge utile
// ---------------------------------------------------------------------------

/**
 * État du formulaire : des chaînes, jamais `null`.
 *
 * Un `input` contrôlé sur `null` repasse en champ non contrôlé et React s'en
 * plaint ; la chaîne vide est traduite en `null` au moment de l'envoi.
 */
interface Etat {
  nom: string;
  prenom: string;
  dateNaissance: string;
  lieuNaissance: string;
  sexe: string;
  nationalite: string;
  ine: string;
  email: string;
  telephone: string;
  adresse: string;
  codePostal: string;
  ville: string;
  siteCode: string;
  mode: ModeDossier;
  formationCode: string;
  conseillerId: string;
  /** Nom du référent chargé, pour le garder lisible s'il sort du périmètre. */
  conseillerNom: string;
  dateEntree: string;
  dateFinPrevue: string;
  mobilite: string;
  hebergeParTiers: boolean;
  rqth: boolean;
  dejaApprenti: boolean;
  demandeurEmploi: boolean;
  titulaireDiplome: boolean;
  rechercheEntreprise: boolean;
  carteEtudianteFaite: boolean;

  // Bloc « L'apprenti(e) » du CERFA. Sans objet en mode OF, où il n'est ni
  // affiché ni envoyé : l'API ne le renvoie même pas sur ces dossiers.
  nomUsage: string;
  nir: string;
  departementNaissance: string;
  regimeSocial: string;
  sportifHautNiveau: boolean;
  rqthEquivalenceJeunes: boolean;
  rqthExtensionBoe: boolean;
  projetCreationEntreprise: boolean;
  numeroVoie: string;
  voie: string;
  complementAdresse: string;
  situationAvantContrat: string;
  dernierDiplomePrepare: string;
  derniereClasseSuivie: string;
  intitulePrecisDernierDiplome: string;
  diplomeLePlusEleve: string;
  representantNom: string;
  representantPrenom: string;
  representantNumeroVoie: string;
  representantVoie: string;
  representantComplement: string;
  representantCodePostal: string;
  representantCommune: string;
  representantCourriel: string;
}

/** `AAAA-MM-JJ` attendu par un `input type="date"`, depuis l'ISO de l'API. */
function jourSaisissable(valeur: string | null): string {
  return valeur ? valeur.slice(0, 10) : '';
}

function etatDepuisDossier(dossier: Dossier): Etat {
  return {
    nom: dossier.identite.nom,
    prenom: dossier.identite.prenom,
    dateNaissance: jourSaisissable(dossier.identite.dateNaissance),
    lieuNaissance: dossier.identite.lieuNaissance ?? '',
    sexe: dossier.identite.sexe ?? '',
    nationalite: dossier.identite.nationalite ?? '',
    ine: dossier.identite.ine ?? '',
    email: dossier.identite.email ?? '',
    telephone: dossier.identite.telephone ?? '',
    adresse: dossier.identite.adresse ?? '',
    codePostal: dossier.identite.codePostal ?? '',
    ville: dossier.identite.ville ?? '',
    siteCode: dossier.dossier.site.code,
    mode: dossier.dossier.mode,
    formationCode: dossier.dossier.formation?.code ?? '',
    conseillerId: dossier.dossier.conseiller?.id ?? '',
    conseillerNom: dossier.dossier.conseiller
      ? `${dossier.dossier.conseiller.prenom} ${dossier.dossier.conseiller.nom}`
      : '',
    dateEntree: jourSaisissable(dossier.dossier.dateEntree),
    dateFinPrevue: jourSaisissable(dossier.dossier.dateFinPrevue),
    mobilite: dossier.dossier.mobilite ?? '',
    hebergeParTiers: dossier.situation.hebergeParTiers,
    rqth: dossier.situation.rqth,
    dejaApprenti: dossier.situation.dejaApprenti,
    demandeurEmploi: dossier.situation.demandeurEmploi,
    titulaireDiplome: dossier.situation.titulaireDiplome,
    rechercheEntreprise: dossier.dossier.rechercheEntreprise,
    carteEtudianteFaite: dossier.dossier.carteEtudianteFaite,

    nomUsage: dossier.cerfa?.nomUsage ?? '',
    nir: dossier.cerfa?.nir ? formaterNir(dossier.cerfa.nir) : '',
    departementNaissance: dossier.cerfa?.departementNaissance ?? '',
    regimeSocial: dossier.cerfa?.regimeSocial ?? '',
    sportifHautNiveau: dossier.cerfa?.sportifHautNiveau ?? false,
    rqthEquivalenceJeunes: dossier.cerfa?.rqthEquivalenceJeunes ?? false,
    rqthExtensionBoe: dossier.cerfa?.rqthExtensionBoe ?? false,
    projetCreationEntreprise: dossier.cerfa?.projetCreationEntreprise ?? false,
    numeroVoie: dossier.cerfa?.numeroVoie ?? '',
    voie: dossier.cerfa?.voie ?? '',
    complementAdresse: dossier.cerfa?.complementAdresse ?? '',
    situationAvantContrat: dossier.cerfa?.situationAvantContrat ?? '',
    dernierDiplomePrepare: dossier.cerfa?.dernierDiplomePrepare ?? '',
    derniereClasseSuivie: dossier.cerfa?.derniereClasseSuivie ?? '',
    intitulePrecisDernierDiplome: dossier.cerfa?.intitulePrecisDernierDiplome ?? '',
    diplomeLePlusEleve: dossier.cerfa?.diplomeLePlusEleve ?? '',
    representantNom: dossier.cerfa?.representant.nom ?? '',
    representantPrenom: dossier.cerfa?.representant.prenom ?? '',
    representantNumeroVoie: dossier.cerfa?.representant.numeroVoie ?? '',
    representantVoie: dossier.cerfa?.representant.voie ?? '',
    representantComplement: dossier.cerfa?.representant.complement ?? '',
    representantCodePostal: dossier.cerfa?.representant.codePostal ?? '',
    representantCommune: dossier.cerfa?.representant.commune ?? '',
    representantCourriel: dossier.cerfa?.representant.courriel ?? '',
  };
}

/**
 * N'envoie que ce qui a bougé.
 *
 * La mise à jour est partielle côté API : transmettre l'ensemble du formulaire
 * ferait réécrire — et journaliser — des champs que personne n'a touchés, et
 * recalculerait la minorité et les pièces attendues sans raison. Une chaîne
 * vidée devient `null`, sauf sur les champs que l'API refuse de vider.
 */
function chargeUtile(valeurs: Etat, initial: Etat): EntreeModificationDossier {
  const charge: EntreeModificationDossier = {};

  /** Champ texte effaçable : la chaîne vide se traduit en `null`. */
  const texte = (cle: 'lieuNaissance' | 'nationalite' | 'ine' | 'email' | 'telephone' | 'adresse' | 'codePostal' | 'ville' | 'dateNaissance' | 'dateEntree' | 'dateFinPrevue'): void => {
    if (valeurs[cle] === initial[cle]) return;
    charge[cle] = valeurs[cle] === '' ? null : valeurs[cle];
  };

  if (valeurs.nom !== initial.nom) charge.nom = valeurs.nom;
  if (valeurs.prenom !== initial.prenom) charge.prenom = valeurs.prenom;
  texte('dateNaissance');
  texte('lieuNaissance');
  if (valeurs.sexe !== initial.sexe) {
    charge.sexe = valeurs.sexe === '' ? null : (valeurs.sexe as 'M' | 'F');
  }
  texte('nationalite');
  texte('ine');
  texte('email');
  texte('telephone');
  texte('adresse');
  texte('codePostal');
  texte('ville');
  if (valeurs.mode !== initial.mode) {
    charge.mode = valeurs.mode;
  }
  // L'antenne et la formation sont des rattachements : l'API les résout par
  // code.
  if (valeurs.siteCode !== initial.siteCode && valeurs.siteCode !== '') {
    charge.siteCode = valeurs.siteCode;
  }
  if (valeurs.formationCode !== initial.formationCode) {
    charge.formationCode = valeurs.formationCode === '' ? null : valeurs.formationCode;
  }
  texte('dateEntree');
  texte('dateFinPrevue');
  if (valeurs.mobilite !== initial.mobilite) {
    charge.mobilite = valeurs.mobilite === '' ? null : (valeurs.mobilite as Mobilite);
  }
  if (valeurs.conseillerId !== initial.conseillerId) {
    charge.conseillerId = valeurs.conseillerId === '' ? null : valeurs.conseillerId;
  }

  for (const cle of [
    'hebergeParTiers',
    'rqth',
    'dejaApprenti',
    'demandeurEmploi',
    'titulaireDiplome',
    'rechercheEntreprise',
    'carteEtudianteFaite',
  ] as const) {
    if (valeurs[cle] !== initial[cle]) charge[cle] = valeurs[cle];
  }

  // Le bloc CERFA n'existe pas en OF : ne rien en envoyer évite d'écrire des
  // rubriques d'apprentissage sur un dossier d'organisme de formation.
  if (valeurs.mode === 'CFA') {
    for (const cle of TEXTES_CERFA) {
      if (valeurs[cle] === initial[cle]) continue;
      charge[cle] = valeurs[cle] === '' ? null : valeurs[cle];
    }
    for (const cle of CASES_CERFA) {
      if (valeurs[cle] !== initial[cle]) charge[cle] = valeurs[cle];
    }
  }

  return charge;
}

/**
 * Coordonnées du représentant légal côté API, sous le nom que chaque champ de
 * l'état porte : le bloc `cerfa.representant` les nomme sans préfixe.
 */
const CLE_REPRESENTANT = {
  representantNom: 'nom',
  representantPrenom: 'prenom',
  representantNumeroVoie: 'numeroVoie',
  representantVoie: 'voie',
  representantComplement: 'complement',
  representantCodePostal: 'codePostal',
  representantCommune: 'commune',
  representantCourriel: 'courriel',
} as const;

/** Coordonnées du représentant légal, qui décident de l'étape à afficher. */
const CHAMPS_REPRESENTANT = [
  'representantNom',
  'representantPrenom',
  'representantNumeroVoie',
  'representantVoie',
  'representantComplement',
  'representantCodePostal',
  'representantCommune',
  'representantCourriel',
] as const;

/** Rubriques CERFA saisies en texte ou choisies dans une liste. */
const TEXTES_CERFA = [
  'nomUsage',
  'nir',
  'departementNaissance',
  'regimeSocial',
  'numeroVoie',
  'voie',
  'complementAdresse',
  'situationAvantContrat',
  'dernierDiplomePrepare',
  'derniereClasseSuivie',
  'intitulePrecisDernierDiplome',
  'diplomeLePlusEleve',
  ...CHAMPS_REPRESENTANT,
] as const;

/** Déclarations du CERFA, cochées ou non : jamais `null`, toujours un booléen. */
const CASES_CERFA = [
  'sportifHautNiveau',
  'rqthEquivalenceJeunes',
  'rqthExtensionBoe',
  'projetCreationEntreprise',
] as const;
